import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { navlinks } from "../../../utils/constants";
import logo from "../../../assets/images/logo.svg";
import { useState, useEffect, useRef } from "react";
import AdminService from "src/services/admin.services";
import { Order } from "src/services/types";

const adminService = new AdminService();

interface LeftPaneProps {
  isSidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
}

function LeftPane({ isSidebarOpen, setSidebarOpen }: LeftPaneProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [imageSrc, setImageSrc] = useState({ show: false, iconSrc: "" });
  const [newOrderCount, setNewOrderCount] = useState<number>(0);
  const [previousOrderCount, setPreviousOrderCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const startTouchX = useRef<number>(0);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await adminService.getnotificationcount(1);
        if (Array.isArray(response)) {
          const filteredOrders = response.filter(
            (order: Order) => order.order_status === "confirmed"
          );
          const newConfirmedOrders = filteredOrders.length;
          const newOrderCount =
            newConfirmedOrders > previousOrderCount
              ? newConfirmedOrders - previousOrderCount
              : 0;

          setNewOrderCount(newOrderCount);
          setPreviousOrderCount(newConfirmedOrders);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [previousOrderCount]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    navigate("/pr-admin/login");
  };

  return (
    <>
      {/* Global style for hidden scrollbar */}
      <style>{`
        .sidebar-scrollable::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
        .sidebar-scrollable {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }
      `}</style>
      
      {/* Sidebar */}
      <nav
        className={`fixed top-0 left-0 h-full w-[250px] bg-white z-40 transform transition-transform duration-300 ease-in-out flex flex-col
           ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
           md:translate-x-0 md:static md:h-full`}
      >
        {/* Logo */}
        <div className="flex justify-center items-center mt-6 flex-shrink-0">
          <img src={logo} alt="logo" className="h-10" />
        </div>

        {/* Nav Links - Scrollable container with hidden scrollbar */}
        <div className="mt-10 px-4 flex-1 overflow-y-auto sidebar-scrollable pb-4 min-h-0">
          {navlinks.map((item, index) => {
            const isTargetRoute = location.pathname.includes(item.to);

            const linkClasses = isTargetRoute
              ? "text-pr-primary flex items-center gap-x-3 py-3 px-4 rounded-lg bg-gray-100"
              : "text-gray-700 hover:text-pr-primary flex items-center gap-x-3 py-3 px-4 rounded-lg hover:bg-gray-50";

            const content = (
              <>
                {item.icon}
                <span className="text-[16px]">{item.name}</span>
                {item.badge && newOrderCount > 0 && (
                  <span className="ml-auto bg-red-600 text-white text-xs rounded-full px-2 py-0.5">
                    {newOrderCount}
                  </span>
                )}
              </>
            );

            return item.to === "logout" ? (
              <button
                key={index}
                onClick={handleLogout}
                className={linkClasses}
              >
                {content}
              </button>
            ) : (
              <NavLink
                to={`/pr-admin/${item.to}`}
                key={index}
                className={linkClasses}
              >
                {content}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Backdrop - only on mobile when sidebar is open */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
}

export default LeftPane;
